/**
 * AdminUsersPage - Admin-Userverwaltung mit Impersonation (v1-Paritaet)
 *
 * Liste aller User (GET /api/v2/admin/users) mit Suche + Premium-Filter +
 * Pagination sowie Login-als-User-Aktion je Zeile
 * (POST /api/v2/admin/users/:id/impersonate). Nur fuer Admins sichtbar
 * (useUserPermissions().isAdmin) - siehe Route routes/_auth.admin.users.tsx.
 *
 * Design-Fix (Dennis-Feedback "user admin ist haesslich"): der vorherige
 * Wurzel-Container hiess `className="page"` — identisch zur globalen
 * AppShell-Klasse `.page` (layout.css: `display:grid;
 * grid-template-columns:252px 1fr; min-height:100vh`, fuer Sidebar+Content).
 * Verschachtelt INNERHALB von AppShell (die selbst schon `.page` traegt)
 * kollabierte dadurch der gesamte Seiteninhalt (Suche/Tabelle/Pagination) in
 * eine 252px-Spalte — exakt das gemeldete Bild ("schmale, abgeschnittene
 * Cards", ueberlappende Kopfzeile/Pagination). Fix: eigener, unbenutzter
 * Klassenname + echtes <table> (Muster wie reports/HotspotRewardTable.tsx:
 * .card mit overflow-x:auto um .table) statt der Grid-Row-Bastelloesung.
 */
import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { Search } from "lucide-react"
import { Pagination } from "../../components/Pagination"
import { useUserPermissions } from "../../hooks/useUserPermissions"
import { useAdminUsers, useImpersonateUser, type AdminUser, type PremiumFilter } from "./useAdminUsers"

const SEARCH_DEBOUNCE_MS = 250
const PREMIUM_FILTER_OPTIONS: Array<{ value: PremiumFilter; key: string }> = [
  { value: undefined, key: "all" },
  { value: "active", key: "premiumActive" },
  { value: "expired", key: "premiumExpired" },
]

function formatDate(value: string | null, locale: string): string {
  if (!value) return ""
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(new Date(value))
}

function PlanCell({ user }: { user: AdminUser }) {
  const { t, i18n } = useTranslation("adminUsers")
  // Ehemals-Premium-User haben is_premium=false, aber ein premium_until in der
  // Vergangenheit — ohne Badge wären sie im "Ehemals Premium"-Filter nicht von
  // Nie-Premium-Usern unterscheidbar (Byzantine-Finding Vollständigkeit).
  if (!user.is_premium && !user.premium_until) {
    return <span>{user.plan}</span>
  }
  const until = user.premium_until ? formatDate(user.premium_until, i18n.language) : null
  return (
    <span>
      {user.plan}
      {" "}
      <span className={user.is_premium ? "pill pill-success" : "pill pill-warning"}>
        {t(user.is_premium ? "list.premiumBadge" : "list.premiumExpiredBadge")}
      </span>
      {until && <span className="muted"> ({until})</span>}
    </span>
  )
}

function AdminUserRow({
  user,
  onImpersonate,
  isPending,
}: {
  user: AdminUser
  onImpersonate: (user: AdminUser) => void
  isPending: boolean
}) {
  const { t, i18n } = useTranslation("adminUsers")
  return (
    <tr data-testid={"admin-user-row-" + user.id}>
      <td>{user.email}</td>
      <td><PlanCell user={user} /></td>
      <td>{user.hotspots_count}</td>
      <td>{formatDate(user.created_at, i18n.language)}</td>
      <td>
        {user.last_sign_in_at ? formatDate(user.last_sign_in_at, i18n.language) : t("list.neverSignedIn")}
      </td>
      <td>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => onImpersonate(user)}
          disabled={isPending}
          data-testid={"admin-user-impersonate-" + user.id}
        >
          {t("list.impersonateAction")}
        </button>
      </td>
    </tr>
  )
}

export function AdminUsersPage() {
  const { t } = useTranslation("adminUsers")
  // TanStack Router types don't fully support string route IDs in from= options;
  // runtime behavior is correct (gleiches Muster wie dashboard/TimespanPicker.tsx).
  const search = useSearch({ from: "/_auth/admin/users" } as unknown as Parameters<typeof useSearch>[0]) as {
    q?: string
    premium?: PremiumFilter
    page: number
  }
  const navigate = useNavigate({ from: "/_auth/admin/users" } as unknown as Parameters<typeof useNavigate>[0]) as unknown as (opts: {
    to: string
    search: (prev: Record<string, unknown>) => Record<string, unknown>
  }) => void
  const { isAdmin, isLoaded } = useUserPermissions()

  const [query, setQuery] = useState(search.q ?? "")
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Suchfeld ist lokal (kein Re-Render der URL bei jedem Tastendruck) und
  // schreibt debounced in die URL — Filterwechsel/Seitenwechsel schreiben
  // direkt. `page` wird bei jeder Aenderung von q/premium auf 1 zurueckgesetzt.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    // Kein Navigate, wenn der lokale Entwurf noch der aktuellen URL entspricht
    // (u.a. beim ersten Mount) — sonst ueberschreibt ein spaeter feuernder
    // Such-Debounce einen dazwischen gesetzten Premium-Filter mit page:1.
    if (query === (search.q ?? "")) return
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      navigate({
        to: "/admin/users",
        search: (prev: Record<string, unknown>) => ({ ...prev, q: query || undefined, page: 1 }),
      })
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  // Rueck-Sync URL -> Suchfeld (Back-/Forward-Button, Byzantine-Finding
  // Korrektheit): aendert sich search.q von aussen, folgt das Eingabefeld.
  // Nicht waehrend eines laufenden Debounce (User tippt gerade) — sonst
  // wuerde ein spaet eintreffendes Navigate den lokalen Entwurf ueberschreiben.
  useEffect(() => {
    if (debounceRef.current) return
    setQuery(search.q ?? "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.q])

  function setPremiumFilter(value: PremiumFilter) {
    navigate({
      to: "/admin/users",
      search: (prev: Record<string, unknown>) => ({ ...prev, premium: value, page: 1 }),
    })
  }

  function setPage(page: number) {
    navigate({ to: "/admin/users", search: (prev: Record<string, unknown>) => ({ ...prev, page }) })
  }

  const { users, pagy, isLoading, isError, refetch } = useAdminUsers(search.q ?? "", search.page, search.premium)
  const impersonate = useImpersonateUser()

  // Nicht-Admins werden nach /dashboard umgeleitet, sobald der Account
  // geladen ist. Waehrend des Ladens (isAdmin noch false, User unbekannt)
  // wird nichts angezeigt - der Backend-Endpunkt ist ohnehin 403-geschuetzt.
  const navigateAway = useNavigate()
  useEffect(() => {
    if (isLoaded && !isAdmin) {
      navigateAway({ to: "/dashboard", replace: true })
    }
  }, [isLoaded, isAdmin, navigateAway])

  if (!isAdmin) return null

  return (
    <div className="admin-users-page" data-testid="admin-users-page">
      <div className="page-header">
        <div>
          <h1>{t("page.title")}</h1>
          <p className="muted" style={{ marginTop: 4 }}>
            {t("page.subtitle")}
          </p>
        </div>
      </div>

      <div className="filterbar">
        <div className="segment" role="group" aria-label={t("filter.ariaLabel")} data-testid="admin-users-premium-filter">
          {PREMIUM_FILTER_OPTIONS.map(({ value, key }) => (
            <button
              key={key}
              type="button"
              className={(search.premium ?? undefined) === value ? "is-active" : ""}
              aria-pressed={(search.premium ?? undefined) === value}
              onClick={() => setPremiumFilter(value)}
              data-testid={"admin-users-premium-" + (value ?? "all")}
            >
              {t("filter." + key)}
            </button>
          ))}
        </div>
        <div className="admin-users-search" style={{ position: "relative", flex: 1, maxWidth: 360 }}>
          <Search aria-hidden="true" size={14} style={{ position: "absolute", left: 10, top: 10 }} />
          <input
            className="input search-input"
            style={{ paddingLeft: 32, width: "100%" }}
            placeholder={t("filter.searchPlaceholder")}
            aria-label={t("filter.searchAria")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-users-search"
          />
        </div>
      </div>

      {isLoading && (
        <div aria-busy="true" aria-label={t("loading")} data-testid="admin-users-loading">
          {t("loading")}
        </div>
      )}

      {isError && (
        <div className="error-state" role="alert" data-testid="admin-users-error">
          <p>{t("error.load")}</p>
          <button type="button" className="btn btn-primary" onClick={() => refetch()}>
            {t("error.retry")}
          </button>
        </div>
      )}

      {!isLoading && !isError && users && users.length === 0 && (
        <div className="empty-state" data-testid="admin-users-empty">
          <p>{t("empty.title")}</p>
        </div>
      )}

      {!isLoading && !isError && users && users.length > 0 && (
        <>
          <div className="card" style={{ overflowX: "auto" }} data-testid="admin-users-list">
            <table className="table" aria-label={t("page.title")}>
              <thead>
                <tr>
                  <th scope="col">{t("list.colEmail")}</th>
                  <th scope="col">{t("list.colPlan")}</th>
                  <th scope="col">{t("list.colHotspots")}</th>
                  <th scope="col">{t("list.colCreatedAt")}</th>
                  <th scope="col">{t("list.colLastSignIn")}</th>
                  <th scope="col">{t("list.colActions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <AdminUserRow
                    key={user.id}
                    user={user}
                    onImpersonate={(u) => impersonate.mutate(u.id)}
                    isPending={impersonate.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={pagy?.page ?? search.page}
            pages={pagy?.pages ?? 1}
            count={pagy?.count ?? users.length}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  )
}
